<template>
  <div>
    <div class="d-flex mb-3 align-items-end">
      <h1 class="h4 primary--text">
        {{ $t('views.file_list.files') }}
      </h1>
      <div class="ml-auto">
        <v-btn
          v-if="business && editor"
          color="primary"
          outlined
          large
          class="btn mr-4"
          @click="showAddModal"
        >
          <v-icon left>
            mdi-plus
          </v-icon>
          {{ $t('common.add') }}
        </v-btn>
        <v-btn
          outlined
          large
          @click="toggleFilter"
        >
          <v-icon left>
            mdi-filter-variant
          </v-icon>
          {{ $t('common.filter') }}
        </v-btn>
      </div>
    </div>
    <files-filter
      v-model="isFilterShown"
      :filter="filter"
      @applyFilter="applyFilter"
    />
    <v-data-table
      fixed-header
      :headers="headers"
      :items="items"
      :footer-props="{
        itemsPerPageOptions: [10, 20, 50, 100],
      }"
      :options.sync="options"
      :server-items-length="total"
      :loading="loading"
      :loading-text="$t('common.loading_data')"
    >
      <template v-slot:item.name="{ item }">
        <a
          :href="item.path"
          :download="item.name"
        >{{ item.name }}</a>
      </template>
      <template v-slot:item.createdAt="{ item }">
        {{ $moment.utc(item.createdAt).format('L LT') }}
      </template>
      <template
        v-if="business && editor"
        v-slot:item.delete="{ item }"
      >
        <v-hover
          v-if="business && editor"
          v-slot:default="{hover}"
        >
          <v-icon
            :class="hover ? '' : 'text--disabled'"
            color="primary"
            size="20"
            @click="showRemoveModal(item)"
          >
            mdi-delete
          </v-icon>
        </v-hover>
      </template>
    </v-data-table>
    <file-adding
      v-model="isAddModalShown"
      @submit="getItems"
    />
    <file-remove
      v-model="isRemoveModalShown"
      :selected-file="selectedFile"
      @submit="getItems"
    />
  </div>
</template>

<script>
import { mapGetters } from 'vuex';
import FileAdding from '@/components/files/FileAdding.vue';
import FilesFilter from '@/components/files/FilesFilter.vue';
import FileRemove from '@/components/files/FileRemove.vue';
import { mongoRegexI } from '@/utils/mongo-regex';

export default {
  name: 'FileList',

  components: {
    FileAdding,
    FilesFilter,
    FileRemove,
  },

  data() {
    return {
      items: [],
      options: {
        page: 1,
      },
      total: 0,

      filter: {
        name: '',
        description: '',
        createdAt: '',
      },

      loading: false,
      isAddModalShown: false,
      isRemoveModalShown: false,
      isFilterShown: false,

      selectedFile: null,
    };
  },

  computed: {
    ...mapGetters(['business', 'editor']),

    headers() {
      const headers = [
        { text: this.$t('views.file_list.file'), value: 'name' },
        { text: this.$t('views.file_list.description'), value: 'description' },
        { text: this.$t('common.date_utc'), value: 'createdAt' },
      ];

      if (this.business && this.editor) {
        headers.push({
          text: this.$t('common.delete'), value: 'delete', sortable: false, width: 150, align: 'center',
        });
      }

      return headers;
    },
  },

  watch: {
    options: {
      handler() {
        this.getItems();
      },
      deep: true,
    },
  },

  methods: {
    async getItems() {
      this.loading = true;

      const {
        sortBy, sortDesc, page, itemsPerPage,
      } = this.options;

      const params = {};
      params.query = this.mapFilter();
      params.pageSize = itemsPerPage;
      params.page = page;
      params.populate = 'blobName';
      if (sortBy.length) {
        params.sort = `${sortDesc[0] ? '-' : ''}${sortBy[0]}`;
      }

      try {
        const { data = {} } = await this.$http.get('/files', { params });
        this.items = (data.rows || []).map((item) => ({
          ...item,
          createdAt: new Date(item.createdAt),
        }));
        this.total = data.total;
      } catch (err) {
        this.$http.mockApiError(err);
      } finally {
        this.loading = false;
      }
    },

    showAddModal() {
      this.isAddModalShown = true;
    },

    showRemoveModal(item) {
      this.selectedFile = item;
      this.isRemoveModalShown = true;
    },

    toggleFilter() {
      this.isFilterShown = !this.isFilterShown;
    },

    applyFilter(filter) {
      this.filter = { ...filter };
      this.options.page = 1;
      this.getItems();
    },

    mapFilter() {
      const filter = {};
      if (this.filter.name) {
        filter.name = mongoRegexI(this.filter.name);
      }
      if (this.filter.description) mongoRegexI(this.filter.description);
      if (this.filter.createdAt) {
        // range separator en="to" ru="—"
        const [start, end] = this.filter.createdAt.replace(' to ', ' — ').split(' — ');
        filter.createdAt = {
          $gte: this.$moment.utc(start),
          $lte: this.$moment.utc(end).endOf('day'),
        };
      }
      return filter;
    },
  },
};
</script>
